import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAnalytics } from '@/hooks/use-analytics';
import { useReasoning } from '@/hooks/use-reasoning';
import { handleError } from '@/lib/error/handle';
import { textModels } from '@/lib/models/text';
import { cn } from '@/lib/utils';
import { getTextFromTextNodes } from '@/lib/xyflow';
import { useProject } from '@/providers/project';
import { useChat } from '@ai-sdk/react';
import { getIncomers, useReactFlow } from '@xyflow/react';
import {
  BotIcon,
  ClockIcon,
  PlayIcon,
  RotateCcwIcon,
  SettingsIcon,
  SquareIcon,
  WrenchIcon,
} from 'lucide-react';
import {
  type ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import { mutate } from 'swr';
import { NodeLayout } from '../layout';
import { ModelSelector } from '../model-selector';
import { AgentResult } from './agent-result';
import type { AgentNodeData, AgentNodeProps } from './index';
import { ToolsDialog } from './tools-dialog';

interface AgentPrimitiveProps extends AgentNodeProps {
  title: string;
}

// Get the default model if none is set
const getDefaultModel = () => {
  return 'anthropic/claude-sonnet-4-0';
};

export function AgentPrimitive({ id, data, title }: AgentPrimitiveProps) {
  const [isConfigExpanded, setIsConfigExpanded] = useState(true);
  const [showToolsDialog, setShowToolsDialog] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { updateNodeData, getNodes, getEdges } = useReactFlow();
  const project = useProject();
  const modelId = data.model ?? getDefaultModel();
  const analytics = useAnalytics();
  const [reasoning, setReasoning] = useReasoning();

  const { append, messages, setMessages, status, stop } = useChat({
    api: '/api/agent',
    body: {
      systemPrompt: data.systemPrompt,
      model: modelId,
      userId: project?.userId || '',
      allowedTools: (data.allowedTools || '')
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      context: '',
    },
    onError: (error) => handleError('Error running agent', error),
    onFinish: (message) => {
      updateNodeData(id, {
        generated: {
          result: message.content,
          status: 'completed',
        },
        updatedAt: new Date().toISOString(),
      });

      setReasoning((oldReasoning) => ({
        ...oldReasoning,
        isGenerating: false,
      }));

      toast.success('Agent completed successfully');

      setTimeout(() => mutate('credits'), 5000);
    },
  });

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, []);

  const handleInputChange = (field: keyof AgentNodeData, value: string) => {
    updateNodeData(id, { [field]: value });
  };

  const handleModelChange = (value: string) => {
    updateNodeData(id, { model: value });
  };

  const handleGenerate = useCallback(async () => {
    if (!data.systemPrompt) {
      handleError('Error running agent', 'System prompt is required');
      return;
    }

    // Get context from incoming text nodes
    const incomers = getIncomers({ id }, getNodes(), getEdges());
    const textContext = getTextFromTextNodes(incomers);
    const contextText = textContext.join('\n');

    analytics.track('canvas', 'node', 'generate', {
      type: 'agent',
      model: modelId,
      systemPromptLength: data.systemPrompt?.length ?? 0,
      toolsCount: (data.allowedTools || '').split(',').filter(Boolean).length,
      contextLength: contextText.length,
    });

    setMessages([]);
    append(
      {
        role: 'user',
        content: contextText,
      },
      {
        body: {
          systemPrompt: data.systemPrompt,
          model: modelId,
          userId: project?.userId || '',
          allowedTools: (data.allowedTools || '')
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean),
          context: contextText,
        },
      }
    );
  }, [
    append,
    data.systemPrompt,
    data.allowedTools,
    modelId,
    analytics,
    setMessages,
    id,
    getNodes,
    getEdges,
    project?.userId,
  ]);

  const selectedTools = (data.allowedTools || '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);

  const toolbar = useMemo(() => {
    const items: ComponentProps<typeof NodeLayout>['toolbar'] = [];

    items.push({
      tooltip: 'Agent Configuration',
      children: (
        <Button
          size="icon"
          className={cn(
            isConfigExpanded
              ? 'rounded-full bg-emerald-500! hover:bg-emerald-500! dark:bg-emerald-400! dark:hover:bg-emerald-400!'
              : 'rounded-full'
          )}
          variant={isConfigExpanded ? 'default' : 'outline'}
          onClick={() => setIsConfigExpanded(!isConfigExpanded)}
        >
          <SettingsIcon size={12} />
        </Button>
      ),
    });

    if (status === 'submitted' || status === 'streaming') {
      items.push({
        tooltip: 'Stop',
        children: (
          <Button
            size="icon"
            className="rounded-full bg-emerald-500! hover:bg-emerald-500! dark:bg-emerald-600! dark:hover:bg-emerald-500!"
            onClick={stop}
            disabled={!project?.id}
          >
            <SquareIcon size={12} />
          </Button>
        ),
      });
    } else if (messages.length || data.generated?.result) {
      items.push({
        tooltip: 'Regenerate',
        children: (
          <Button
            className="rounded-full bg-emerald-500! hover:bg-emerald-500! dark:bg-emerald-600! dark:hover:bg-emerald-500!"
            onClick={handleGenerate}
            disabled={!project?.id || !data.systemPrompt}
          >
            <RotateCcwIcon size={12} />
            Rerun Agent
          </Button>
        ),
      });
    } else {
      items.push({
        tooltip: 'Run Agent',
        children: (
          <Button
            className="rounded-full bg-emerald-500! hover:bg-emerald-500! dark:bg-emerald-600! dark:hover:bg-emerald-500!"
            onClick={handleGenerate}
            disabled={!project?.id || !data.systemPrompt}
          >
            <PlayIcon size={12} />
            Run Agent
          </Button>
        ),
      });
    }

    if (data.updatedAt) {
      items.push({
        tooltip: `Last updated: ${new Intl.DateTimeFormat('en-US', {
          dateStyle: 'short',
          timeStyle: 'short',
        }).format(new Date(data.updatedAt))}`,
        children: (
          <Button size="sm" variant="ghost">
            <ClockIcon size={12} />
          </Button>
        ),
      });
    }

    return items;
  }, [
    isConfigExpanded,
    status,
    messages.length,
    data.generated?.result,
    data.updatedAt,
    data.systemPrompt,
    handleGenerate,
    project?.id,
    stop,
  ]);

  useEffect(() => {
    const hasReasoning = messages.some((message) =>
      message.parts?.some((part) => part.type === 'reasoning')
    );

    if (hasReasoning && !reasoning.isReasoning && status === 'streaming') {
      setReasoning({ isReasoning: true, isGenerating: true });
    }
  }, [messages, reasoning, status, setReasoning]);

  return (
    <>
      <NodeLayout
        id={id}
        type="agent"
        title={title}
        data={data}
        toolbar={toolbar}
        className="node-agent"
      >
        <div className="space-y-4 divide-y">
          {/* Header */}
          <div className="flex items-center gap-2 p-2">
            <div className="relative flex items-center justify-center rounded-lg bg-emerald-200/60 p-2.5 dark:bg-emerald-800/60">
              <BotIcon className="size-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            <Input
              value={data.label || 'Agent'}
              onChange={(e) => handleInputChange('label', e.target.value)}
              className="border-none bg-transparent font-medium text-sm"
              placeholder="Agent name"
            />
          </div>

          {/* Configuration */}
          {isConfigExpanded && (
            <div className="space-y-4 p-3">
              {/* Model Selection */}
              <div>
                <Label className="font-medium text-foreground text-sm">
                  Model
                </Label>
                <div className="mt-1">
                  <ModelSelector
                    reasoning={true}
                    value={modelId}
                    options={textModels}
                    onChange={handleModelChange}
                    className="!w-full"
                  />
                </div>
              </div>

              {/* System Prompt */}
              <div>
                <Label className="font-medium text-foreground text-sm">
                  System Prompt
                </Label>
                <Textarea
                  ref={textareaRef}
                  value={data.systemPrompt || ''}
                  onChange={(e) =>
                    handleInputChange('systemPrompt', e.target.value)
                  }
                  placeholder="Define the agent's role, personality, and capabilities..."
                  className="nowheel mt-1 max-h-[320px] min-h-[100px] resize-none overflow-y-auto"
                />
              </div>

              {/* Tools */}
              <div>
                <div className="flex items-center justify-between">
                  <Label className="font-medium text-foreground text-sm">
                    Tools ({selectedTools.length})
                  </Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowToolsDialog(true)}
                  >
                    <WrenchIcon className="mr-1 size-3" />
                    Configure Tools
                  </Button>
                </div>
                {selectedTools.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {selectedTools.map((tool) => (
                      <Badge key={tool} variant="secondary" className="text-xs">
                        {tool}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <AgentResult
            messages={messages}
            status={status}
            generatedResult={data.generated?.result}
          />
        </div>
      </NodeLayout>

      {/* Tools Dialog */}
      <ToolsDialog
        isOpen={showToolsDialog}
        selectedTools={selectedTools}
        onClose={() => setShowToolsDialog(false)}
        onToolsChange={(tools) => {
          handleInputChange('allowedTools', tools.join(', '));
        }}
      />
    </>
  );
}
